// src/services/taskApi.ts
import axios from 'axios';
import { setupAxiosInterceptors } from '../utils/axiosSetup';

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000',
  timeout: 30000,
  withCredentials: true,
});

// Set up standard interceptors (includes 401 handling)
setupAxiosInterceptors(apiClient);

export interface TaskFilters {
  page?: number;
  pageSize?: number;
  startDate?: string;
  endDate?: string;
  projectId?: number;
  projectIds?: string;
  airportId?: number;
  airportIds?: string;
  userId?: number;
  userIds?: string;
  userType?: string;
  status?: string;
  pastDueOnly?: boolean;
  upcomingDays?: number;
}

export interface Task {
  taskId: number;
  name: string;
  description?: string;
  projectId: number;
  projectName: string;
  phaseId: number;
  phaseName: string;
  airportId?: number;
  airportName?: string;
  contractedStartDate: string;
  contractedEndDate: string;
  actualStartDate?: string;
  actualEndDate?: string;
  status: string;
  isPastDue: boolean;
  daysOverdue?: number;
  daysUntilDeadline?: number;
  assignedUsers: Array<{
    userId: number;
    email: string;
    displayName: string;
    organizationType: string;
  }>;
  actionCount: number;
  completedActionCount: number;
  kpiScore: number;
}

export interface TaskSummary {
  totalTasks: number;
  pastDueCount: number;
  upcomingCount: number;
  inProgressCount: number;
  completeCount: number;
  kpiStats: {
    aggregateKpiScore: number;
    totalActions: number;
    completedActions: number;
    kpiByOrganization: {
      Fraport?: { kpiScore: number; actionCount: number; completedActionCount: number };
      Tenant?: { kpiScore: number; actionCount: number; completedActionCount: number };
      Client?: { kpiScore: number; actionCount: number; completedActionCount: number };
    };
  };
  byAirport?: Array<{ airportId: number; airportName: string; taskCount: number; pastDueCount: number; kpiScore: number }>;
  byProject?: Array<{ projectId: number; projectName: string; taskCount: number; pastDueCount: number; kpiScore: number }>;
  byUser?: Array<{ userId: number; userName: string; taskCount: number; pastDueCount: number; kpiScore: number }>;
}

export interface CrossProjectTasksResponse {
  tasks: Task[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
  summary: TaskSummary;
}

export interface TaskApiError {
  error: boolean;
  message: string;
  code?: 'AUTH_REQUIRED' | 'VALIDATION_ERROR' | 'NETWORK_ERROR';
}

/**
 * Get cross-project tasks with filters
 */
export async function getCrossProjectTasks(filters: TaskFilters): Promise<CrossProjectTasksResponse> {
  try {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, String(value));
      }
    });

    const url = `/api/tasks/cross-project?${params.toString()}`;
    console.log('Fetching tasks from:', url);
    console.log('Filters:', filters);

    const response = await apiClient.get(url);
    return response.data;
  } catch (error: any) {
    // Log full error details for debugging
    console.error('Error fetching cross-project tasks:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: error.message,
      url: error.config?.url,
    });

    if (error.response?.status === 401) {
      throw {
        error: true,
        message: 'Authentication required',
        code: 'AUTH_REQUIRED',
      } as TaskApiError;
    }
    if (error.response?.status === 400) {
      throw {
        error: true,
        message: error.response.data?.message || 'Validation error',
        code: 'VALIDATION_ERROR',
      } as TaskApiError;
    }
    if (error.response?.status === 500) {
      const errorMessage = error.response.data?.message || error.response.data?.error || 'Internal server error';
      throw {
        error: true,
        message: `Server error: ${errorMessage}`,
        code: 'NETWORK_ERROR',
      } as TaskApiError;
    }
    throw {
      error: true,
      message: error.message || 'Failed to fetch tasks',
      code: 'NETWORK_ERROR',
    } as TaskApiError;
  }
}

/**
 * Get task summary with filters
 */
export async function getTaskSummary(filters: TaskFilters): Promise<{ summary: TaskSummary }> {
  try {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, String(value));
      }
    });

    const url = `/api/tasks/summary?${params.toString()}`;
    console.log('Fetching task summary from:', url);

    const response = await apiClient.get(url);
    return response.data;
  } catch (error: any) {
    // Log full error details for debugging
    console.error('Error fetching task summary:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: error.message,
      url: error.config?.url,
    });

    if (error.response?.status === 401) {
      throw {
        error: true,
        message: 'Authentication required',
        code: 'AUTH_REQUIRED',
      } as TaskApiError;
    }
    if (error.response?.status === 400) {
      throw {
        error: true,
        message: error.response.data?.message || 'Validation error',
        code: 'VALIDATION_ERROR',
      } as TaskApiError;
    }
    if (error.response?.status === 500) {
      const errorMessage = error.response.data?.message || error.response.data?.error || 'Internal server error';
      throw {
        error: true,
        message: `Server error: ${errorMessage}`,
        code: 'NETWORK_ERROR',
      } as TaskApiError;
    }
    throw {
      error: true,
      message: error.message || 'Failed to fetch task summary',
      code: 'NETWORK_ERROR',
    } as TaskApiError;
  }
}

